import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Plus, Search } from "lucide-react";

export interface EmailVariable {
  token: string;
  label: string;
  description: string;
  group: VariableGroup;
}

type VariableGroup =
  | "Recipient"
  | "Event"
  | "Money"
  | "Invoice"
  | "Tour"
  | "Media"
  | "Sender";

const GROUP_ORDER: VariableGroup[] = [
  "Recipient",
  "Event",
  "Money",
  "Invoice",
  "Tour",
  "Media",
  "Sender",
];

/**
 * Canonical, deduped picker list.
 *
 * Every token in this list is verified active in the server-side merge maps
 * (see `server/routes.ts` confirmation + reminder flows, and
 * `server/lib/event-series-generator.ts`).
 *
 * Canonical tokens: `{{event_date}}` and `{{event_name}}` are the preferred
 * tokens reps should use going forward (Task #125). Legacy aliases
 * (`{{date}}`, `{{booking_details}}`) are intentionally surfaced here as
 * "(legacy)" so existing templates continue to work and reps can see both.
 * Other server-expanded aliases that are purely internal (e.g. `{{client_name}}`
 * = `{{account_name}}`, `{{total}}` / `{{booking_total}}`, camelCase variants)
 * are NOT shown — those exist solely for backward compatibility with stored
 * templates and would cause confusion if offered as choices.
 *
 * `{{fee}}` is also omitted on purpose — it resolves to balance_due in the
 * confirmation flow but to event_rate in the series-generator flow, so
 * recommending it would be ambiguous. Use `{{event_rate}}` or `{{balance_due}}`
 * directly.
 */
export const EMAIL_VARIABLES: EmailVariable[] = [
  // Recipient
  { token: "{{contact_name}}", label: "Contact Name", description: "Primary contact's full name", group: "Recipient" },
  { token: "{{contactFirstName}}", label: "Contact First Name", description: "First name only", group: "Recipient" },
  { token: "{{account_name}}", label: "Account Name", description: "Business / venue / organization name", group: "Recipient" },

  // Event
  { token: "{{event_name}}", label: "Event Name", description: "Event description / performance summary", group: "Event" },
  { token: "{{event_date}}", label: "Event Date", description: "Date of the event", group: "Event" },
  { token: "{{booking_details}}", label: "Event Details (legacy)", description: "Event description / performance summary", group: "Event" },
  { token: "{{date}}", label: "Event Date (legacy)", description: "Date of the event", group: "Event" },
  { token: "{{event_time}}", label: "Event Time", description: "Time of the event", group: "Event" },
  { token: "{{location}}", label: "Location", description: "Venue address", group: "Event" },
  { token: "{{fullAddress}}", label: "Full Address", description: "Complete address with city / state", group: "Event" },
  { token: "{{event_offering}}", label: "Event Offering", description: "Type / package booked", group: "Event" },
  { token: "{{talent}}", label: "Talent", description: "Talent name", group: "Event" },
  { token: "{{google_review_url}}", label: "Google Review Link", description: "Link to talent's Google review page", group: "Event" },

  // Money
  { token: "{{event_rate}}", label: "Event Rate", description: "Original event rate before discount", group: "Money" },
  { token: "{{discount}}", label: "Discount", description: "Discount applied (blank if none)", group: "Money" },
  { token: "{{amount}}", label: "Invoice Amount", description: "Rate minus discount", group: "Money" },
  { token: "{{balance_due}}", label: "Balance Due", description: "Amount the client still owes", group: "Money" },
  { token: "{{deposit_amount}}", label: "Deposit Amount", description: "Deposit portion of the invoice", group: "Money" },
  { token: "{{deposit_due_date}}", label: "Deposit Due Date", description: "When the deposit is due", group: "Money" },
  { token: "{{balance_due_date}}", label: "Balance Due Date", description: "When the remaining balance is due", group: "Money" },

  // Invoice
  { token: "{{invoice_number}}", label: "Invoice #", description: "Invoice number", group: "Invoice" },
  { token: "{{invoice_url}}", label: "Invoice Link", description: "Public link to view / pay the invoice", group: "Invoice" },

  // Tour
  { token: "{{run_summary}}", label: "Tour Run Summary", description: "Every event in the tour run, oldest first", group: "Tour" },
  { token: "{{talent_breakdown}}", label: "Talent Breakdown", description: "Talent lineup for this event with role", group: "Tour" },

  // Media
  { token: "{{media_assets_url}}", label: "Media Link", description: "Link to artist's promo / media assets", group: "Media" },

  // Sender
  { token: "{{sender_name}}", label: "Sender Name", description: "Your name", group: "Sender" },
];

interface EmailVariablesSelectorProps {
  onInsert: (variable: string) => void;
  variables?: EmailVariable[];
}

export function EmailVariablesSelector({ onInsert, variables = EMAIL_VARIABLES }: EmailVariablesSelectorProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? variables.filter(
          (v) =>
            v.label.toLowerCase().includes(q) ||
            v.token.toLowerCase().includes(q) ||
            v.description.toLowerCase().includes(q),
        )
      : variables;
    const map = new Map<VariableGroup, EmailVariable[]>();
    for (const v of filtered) {
      if (!map.has(v.group)) map.set(v.group, []);
      map.get(v.group)!.push(v);
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ group: g, items: map.get(g)! }));
  }, [query, variables]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          type="button"
          data-testid="button-insert-variable"
        >
          <Plus className="w-4 h-4 mr-1" />
          Variables
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 flex flex-col"
        style={{ maxHeight: "min(70vh, 480px)" }}
        align="end"
        side="bottom"
        sideOffset={5}
        collisionPadding={16}
        avoidCollisions
      >
        <div className="p-2 border-b shrink-0">
          <p className="text-sm font-medium">Insert Variable</p>
          <p className="text-xs text-muted-foreground">Click to add to your message</p>
          <div className="relative mt-2">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              autoFocus={false}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search variables…"
              className="h-8 pl-7 text-sm"
              data-testid="input-variable-search"
            />
          </div>
        </div>
        <div
          className="flex-1 overflow-y-auto p-1"
          style={{
            WebkitOverflowScrolling: "touch",
            overscrollBehavior: "contain",
            touchAction: "pan-y",
          }}
          data-testid="list-variables"
        >
          {grouped.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">
              No variables match "{query}".
            </p>
          ) : (
            grouped.map(({ group, items }) => (
              <div key={group} className="mb-1 last:mb-0">
                <div className="sticky top-0 bg-popover/95 backdrop-blur-sm px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground z-10">
                  {group}
                </div>
                {items.map((variable) => (
                  <Button
                    key={variable.token}
                    variant="ghost"
                    type="button"
                    className="w-full justify-start h-auto py-1.5 px-2"
                    onClick={() => {
                      onInsert(variable.token);
                      setOpen(false);
                      setQuery("");
                    }}
                    data-testid={`variable-${variable.token.replace(/[{}]/g, "")}`}
                  >
                    <div className="text-left w-full">
                      <div className="font-medium text-sm">{variable.label}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {variable.token}
                      </div>
                      <div className="text-[11px] text-muted-foreground/80 leading-tight mt-0.5">
                        {variable.description}
                      </div>
                    </div>
                  </Button>
                ))}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
